import { useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Alert, CircularProgress, Typography,
} from '@mui/material';
import api from '../api/axios';
import {
  arrayPaths, sampleKeys, detectMapping, withExtraFields, buildBoard, formatScore, FORMATS,
} from './leaderboard';

// Add or edit a dashboard's data source. The URL is fetched once ("Test"),
// fields are auto-detected, and the user can correct the mapping before saving.
export default function SourceDialog({ open, onClose, onSave, initial, saving }) {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [sample, setSample] = useState(null);
  const [mapping, setMapping] = useState(null);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');

  const handleEnter = () => {
    setUrl(initial?.url || '');
    setLabel(initial?.label || '');
    setSample(null);
    setMapping(null);
    setError('');
    if (initial?.url) test(initial.url, initial.mapping);
  };

  async function test(target = url, keepMapping = null) {
    setTesting(true);
    setError('');
    try {
      const { data } = await api.post('/dashboards/preview-source', { url: target.trim() });
      setSample(data.data);
      setMapping(keepMapping?.scoreField ? keepMapping : detectMapping(data.data));
    } catch (e) {
      setSample(null);
      setMapping(null);
      setError(e.response?.data?.error || e.message);
    } finally {
      setTesting(false);
    }
  }

  const update = (patch) => {
    let next = { ...mapping, ...patch };
    if ('rowsPath' in patch) next = detectMapping(sample, patch.rowsPath);
    setMapping(withExtraFields(sample, next));
  };

  const board = sample && mapping ? buildBoard(sample, mapping) : null;
  const keys = sample ? sampleKeys(sample, mapping?.rowsPath) : [];
  const paths = sample ? arrayPaths(sample) : [];
  const canSave = board?.rows.length > 0 && url.trim() && !testing;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle>{initial?.url ? 'Edit data source' : 'Add data source'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              label="JSON endpoint URL" value={url} autoFocus fullWidth size="small"
              placeholder="https://example.com/api/v1/leaderboard"
              onChange={(e) => { setUrl(e.target.value); setSample(null); setMapping(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && url.trim()) test(); }}
            />
            <Button variant="outlined" onClick={() => test()} disabled={!url.trim() || testing}
              sx={{ flexShrink: 0, height: 40 }}
            >
              {testing ? <CircularProgress size={16} /> : 'Test'}
            </Button>
          </Box>
          <TextField label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)}
            size="small" fullWidth placeholder="e.g. Speedrun — production"
          />

          {error && <Alert severity="error" variant="outlined">{error}</Alert>}

          {mapping && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1, mt: 0.5 }}>
                Field mapping
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <TextField select size="small" label="Rows" value={mapping.rowsPath}
                  onChange={(e) => update({ rowsPath: e.target.value })}
                >
                  {paths.map((p) => <MenuItem key={p} value={p}>{p || '(root array)'}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Player" value={mapping.nameField}
                  onChange={(e) => update({ nameField: e.target.value })}
                >
                  {keys.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Score" value={mapping.scoreField}
                  onChange={(e) => update({ scoreField: e.target.value })}
                >
                  {keys.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Rank (optional)" value={mapping.rankField}
                  onChange={(e) => update({ rankField: e.target.value })}
                >
                  <MenuItem value="">Compute from score</MenuItem>
                  {keys.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Format" value={mapping.format}
                  onChange={(e) => update({ format: e.target.value })}
                >
                  {FORMATS.map((f) => <MenuItem key={f.id} value={f.id}>{f.label}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Better is" value={mapping.order}
                  onChange={(e) => update({ order: e.target.value })}
                >
                  <MenuItem value="asc">Lower (fastest time)</MenuItem>
                  <MenuItem value="desc">Higher (most points)</MenuItem>
                </TextField>
              </Box>
              {board?.stats ? (
                <Alert severity="success" variant="outlined">
                  {board.stats.count} rows · leader <b>{board.stats.leader}</b> with{' '}
                  {formatScore(board.stats.best, mapping.format)}
                  {mapping.extraFields.length > 0 && ` · extra columns: ${mapping.extraFields.join(', ')}`}
                </Alert>
              ) : (
                <Alert severity="warning" variant="outlined">
                  {board?.error || 'No rows with a numeric score — check the Score field.'}
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="text" color="inherit">Cancel</Button>
        <Button variant="contained" disabled={!canSave || saving}
          onClick={() => onSave({ url: url.trim(), label: label.trim(), mapping })}
        >
          {saving ? 'Saving…' : 'Save source'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
